import React, { useEffect, useState } from 'react';
import './Login.scss';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { loginWithGoogle } from '../services/auth';
import { FirebaseError } from 'firebase/app';

// Short, friendly FR messages
const friendlyAuthError = (code?: string): string => {
  const map: Record<string, string> = {
    'auth/invalid-credential': 'Identifiants incorrects.',
    'auth/wrong-password': 'Mot de passe incorrect.',
    'auth/user-not-found': 'Aucun compte avec cet e-mail.',
    'auth/invalid-email': 'Adresse e-mail invalide.',
    'auth/too-many-requests': 'Trop de tentatives. Réessayez plus tard.',
    'auth/user-disabled': 'Compte désactivé.',
    'auth/network-request-failed': 'Problème réseau. Réessayez.',
    'auth/operation-not-allowed': 'Connexion non disponible.',
    'auth/popup-closed-by-user': 'Fenêtre fermée.',
    'auth/cancelled-popup-request': 'Fenêtre déjà ouverte.',
    'auth/popup-blocked': 'Fenêtre bloquée par le navigateur.',
  };
  return map[code || ''] || 'Impossible de vous connecter. Réessayez.';
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-hide errors after 6s
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // const token = await auth.currentUser?.getIdToken();
      window.location.href = '/dashboard';
    } catch (err) {
      const fe = err as FirebaseError;
      console.error('Firebase login error:', fe.code, fe.message);
      setError(friendlyAuthError(fe.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      await loginWithGoogle();
      // const token = await auth.currentUser?.getIdToken();
      window.location.href = '/dashboard';
    } catch (err) {
      const fe = err as FirebaseError;
      console.error('Firebase Google login error:', fe.code, fe.message);
      setError(friendlyAuthError(fe.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <div className="loginPage">
        <div className="topSection">
          <div className="card">
            <h2 className="title">Connexion</h2>

            <form className="form" onSubmit={handleSubmit} noValidate>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input"
                required
                autoComplete="email"
              />

              <div className="inputWrap">
                <input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Mot de passe"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="toggleBtn"
                  onClick={() => setShowPwd(v => !v)}
                  aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPwd ? (
                    // eye-off 24px
                    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M2 2l20 20" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <path d="M1.5 12S4.5 5 12 5 22.5 12 22.5 12 20.8 15.9 17 18.3" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  ) : (
                    // eye
                    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M1.5 12S4.5 5 12 5s10.5 7 10.5 7-3 7-10.5 7S1.5 12 1.5 12z" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </button>
              </div>

              <button type="submit" className="submitBtn" disabled={loading}>
                {loading ? 'Connexion…' : 'Se connecter'}
              </button>
            </form>

            <button
              type="button"
              onClick={handleGoogle}
              className="googleBtn"
              disabled={loading}
              aria-label="Se connecter avec Google"
            >
              <svg width="20" height="20" viewBox="0 0 48 48" style={{ marginRight: 8 }}>
                <g>
                  <path fill="#4285F4" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.23l6.85-6.85C35.64 2.09 30.13 0 24 0 14.82 0 6.73 5.48 2.69 13.44l7.98 6.2C12.13 13.13 17.62 9.5 24 9.5z"/>
                  <path fill="#34A853" d="M46.1 24.55c0-1.64-.15-3.22-.42-4.74H24v9.01h12.42c-.54 2.9-2.18 5.36-4.65 7.01l7.19 5.6C43.93 37.36 46.1 31.44 46.1 24.55z"/>
                  <path fill="#FBBC05" d="M10.67 28.09c-1.01-2.9-1.01-6.08 0-8.98l-7.98-6.2C.64 16.36 0 20.09 0 24s.64 7.64 2.69 10.09l7.98-6.2z"/>
                  <path fill="#EA4335" d="M24 48c6.13 0 11.64-2.03 15.53-5.53l-7.19-5.6c-2.01 1.35-4.59 2.13-8.34 2.13-6.38 0-11.87-3.63-14.33-8.93l-7.98 6.2C6.73 42.52 14.82 48 24 48z"/>
                  <path fill="none" d="M0 0h48v48H0z"/>
                </g>
              </svg>
              Se connecter avec Google
            </button>

            <div className="signupLink">
              Pas de compte ? <a href="/signup" className="signupAnchor">Inscription</a>
            </div>

            {error && <div className="error">{error}</div>}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
